import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Application, ApplicationStatus, Applicant } from '../types';
import MyScholarshipPanel from '../components/MyScholarshipPanel';
import { toTitleCase } from '../utils/formatName';
import Modal from '../components/Modal';

const DELETABLE_STATUSES = new Set<ApplicationStatus>([
  ApplicationStatus.DRAFT,
  ApplicationStatus.SUBMITTED,
  ApplicationStatus.UNDER_REVIEW,
  ApplicationStatus.DOCUMENT_VERIFICATION,
  ApplicationStatus.INTERVIEW,
  ApplicationStatus.NEEDS_REVISION
]);

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  [ApplicationStatus.DRAFT]: 'Draft',
  [ApplicationStatus.SUBMITTED]: 'Submitted',
  [ApplicationStatus.UNDER_REVIEW]: 'Under Review',
  [ApplicationStatus.DOCUMENT_VERIFICATION]: 'Document Verification',
  [ApplicationStatus.INTERVIEW]: 'Interview',
  [ApplicationStatus.APPROVED]: 'Approved',
  [ApplicationStatus.REJECTED]: 'Rejected',
  [ApplicationStatus.NEEDS_REVISION]: 'Needs Revision'
};

const STATUS_BADGE: Record<ApplicationStatus, string> = {
  [ApplicationStatus.DRAFT]: 'badge-secondary',
  [ApplicationStatus.SUBMITTED]: 'badge-primary',
  [ApplicationStatus.UNDER_REVIEW]: 'badge-primary',
  [ApplicationStatus.DOCUMENT_VERIFICATION]: 'badge-warning',
  [ApplicationStatus.INTERVIEW]: 'badge-warning',
  [ApplicationStatus.APPROVED]: 'badge-success',
  [ApplicationStatus.REJECTED]: 'badge-error',
  [ApplicationStatus.NEEDS_REVISION]: 'badge-warning'
};

function formatDate(value?: string | Date) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const MyApplicationPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const applyNotice = (location.state as { applyNotice?: string } | null)?.applyNotice;
  const [applications, setApplications] = useState<Application[]>([]);
  const [applicant, setApplicant] = useState<Applicant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [deletingApplication, setDeletingApplication] = useState<Application | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = async () => {
    setError('');
    try {
      const [applicationsResult, profile] = await Promise.all([
        apiService.getApplications({ pageSize: 50 }),
        apiService.getMyProfile()
      ]);
      setApplications(applicationsResult.data);
      setApplicant(profile);
    } catch (err: any) {
      setError(err.message || 'Failed to load your applications');
    } finally {
      setLoading(false);
    }
  };

  // Editing (and, in MyScholarshipPanel, requesting a renewal) is only
  // available once a student has an application to edit against and its
  // program is still open — locked automatically on submission
  // (ApplicationService.submitApplication) until an Admin/Super Admin
  // re-enables it, and additionally gated on the program itself still
  // being open even once unlocked. A student with no applications yet
  // (still filling out their profile for the first time) is unaffected —
  // there's no program to check yet.
  const hasOpenProgram = applications.length === 0 || applications.some((a) => a.scholarshipStatus === 'active');
  const canEditProfile = !applicant?.profileLocked && hasOpenProgram;

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (applicationId: number) => {
    setBusyId(applicationId);
    setError('');
    try {
      await apiService.submitApplication(applicationId);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to submit application');
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingApplication) return;
    setDeleting(true);
    setError('');
    try {
      await apiService.deleteApplication(deletingApplication.id);
      setDeletingApplication(null);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete application');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <nav className="navbar">
        <div className="navbar-brand">My Application</div>
        <div className="navbar-actions">
          {canEditProfile ? (
            <Link to="/profile" className="btn btn-outline btn-sm">
              Edit My Information
            </Link>
          ) : (
            <button
              className="btn btn-outline btn-sm"
              type="button"
              disabled
              title={
                !applicant?.profileLocked
                  ? "Editing is unavailable — none of your applications' programs are currently open."
                  : 'Your profile is locked because you submitted an application. Ask an administrator to unlock it.'
              }
            >
              Edit My Information
            </button>
          )}
        </div>
      </nav>

      <div className="container">
        <div className="page-header">
          <h1>Welcome, {toTitleCase(user?.firstName)}</h1>
          <p>Apply for scholarships and track your application status here.</p>
        </div>

        {/* Set by ProgramPage when an application was just created — applying
            only creates it, so this is the prompt to actually submit. */}
        {applyNotice && (
          <div
            style={{
              padding: '1rem',
              background: 'rgba(139, 92, 246, 0.08)',
              border: '1px solid rgba(139, 92, 246, 0.35)',
              borderRadius: 'var(--radius-md)',
              marginBottom: '1.5rem',
              fontSize: '0.9rem'
            }}
          >
            {applyNotice}
          </div>
        )}

        {error && (
          <div
            className="alert-error"
            style={{ padding: '1rem', marginBottom: '1.5rem' }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <MyScholarshipPanel profileLocked={applicant?.profileLocked ?? false} />

            <section style={{ marginBottom: '3rem' }}>
              <div className="card">
                <div className="card-header">
                  <h3>My Applications</h3>
                </div>

                {applications.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)' }}>
                    You haven't applied to any scholarship yet. Browse open programs on the{' '}
                    <Link to="/programs">Programs</Link> page.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {applications.map((application) => (
                      <div
                        key={application.id}
                        style={{
                          padding: '1rem',
                          background: 'rgba(139, 92, 246, 0.05)',
                          borderRadius: '12px',
                          borderLeft: '3px solid #8B5CF6'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div>
                            <strong style={{ color: 'var(--text-primary)' }}>{application.scholarshipName ?? `Scholarship #${application.scholarshipId}`}</strong>
                            <div style={{ marginTop: '0.35rem' }}>
                              <span className={`badge ${STATUS_BADGE[application.status]}`}>
                                {STATUS_LABEL[application.status]}
                              </span>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                            <div>Applied: {formatDate(application.createdAt)}</div>
                            {application.submissionDate && <div>Submitted: {formatDate(application.submissionDate)}</div>}
                          </div>
                        </div>

                        {application.comments && (
                          <p style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                            <strong>Reviewer note:</strong> {application.comments}
                          </p>
                        )}

                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                          {(application.status === ApplicationStatus.DRAFT ||
                            application.status === ApplicationStatus.NEEDS_REVISION) && (
                            <button
                              className="btn btn-primary btn-sm"
                              disabled={busyId === application.id}
                              onClick={() => handleSubmit(application.id)}
                            >
                              {busyId === application.id ? 'Submitting...' : 'Submit Application'}
                            </button>
                          )}

                          {DELETABLE_STATUSES.has(application.status) && (
                            <button
                              className="btn btn-outline btn-sm"
                              style={{ color: 'var(--error-text)', borderColor: 'var(--error-text)' }}
                              disabled={busyId === application.id}
                              onClick={() => setDeletingApplication(application)}
                            >
                              Delete Application
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      {deletingApplication && (
        <Modal title="Delete Application" onClose={() => setDeletingApplication(null)}>
          <p className="alert-error" style={{ padding: '0.85rem' }}
          >
            Delete your application to <strong>{deletingApplication.scholarshipName ?? 'this scholarship'}</strong>?
            This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button
              className="btn btn-primary"
              type="button"
              style={{ background: 'var(--error-text)' }}
              disabled={deleting}
              onClick={handleConfirmDelete}
            >
              {deleting ? 'Deleting...' : 'Delete Application'}
            </button>
            <button className="btn btn-outline" type="button" onClick={() => setDeletingApplication(null)} disabled={deleting}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MyApplicationPage;
